import {
  Button,
  LoadingSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiMapPin, PiPolygon } from 'react-icons/pi';
import React, { useMemo } from 'react';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { DrawGeometryType } from '@ais-ui/react-openlayers';
import { useMapSnapshot } from './useMapSnapshot';
import { cn, createGeoJsonFromGeometry, Geometry } from '@ais-ui/utils';
import { UseFormReturn } from 'react-hook-form';
import { TextGeometryPopover } from './TextGeometryPopover';
import { useLocalization } from '@ais-ui/locale-management';

interface DrawPolygonFieldProps {
  form?: UseFormReturn<any>;
  geometry: Geometry | null;
  onStartDrawing: (
    geometryType: DrawGeometryType,
    shape?: GeoJson | undefined | null
  ) => void;
  color: string,
}

export const DrawPolygonField = ({
  form,
  geometry,
  onStartDrawing,
  color,
}: DrawPolygonFieldProps) => {
  const { messages } = useLocalization();
  const shape = useMemo(() => {
    if (geometry) {
      return createGeoJsonFromGeometry(geometry);
    }
    return null;
  }, [geometry]);

  const { snapshot, isLoading } = useMapSnapshot(shape, color);

  return (
    <div
      className={cn(
        'flex space-x-4 justify-center',
        !snapshot && !isLoading && 'justify-start'
      )}
    >
      {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
      {!snapshot && !isLoading && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10"
          onClick={() => onStartDrawing('Polygon', shape)}
        >
          <PiPolygon className="h-8 w-8"></PiPolygon>
        </Button>
      )}
      {snapshot && !isLoading && (
        <div className="relative">
          <img
            alt={`Shape Preview`}
            className="rounded-md"
            height="160px"
            src={snapshot}
            style={{
              objectFit: 'cover',
            }}
            width="420px"
          />

          <div className="absolute bottom-1 right-1 flex flex-row-reverse space-x-reverse space-x-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  id="draw-edit-shape-button"
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => onStartDrawing('Polygon', shape)}
                >
                  <PiPolygon className="h-8 w-8"></PiPolygon>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {messages.textDrawingFromDialogButtonText}
              </TooltipContent>
            </Tooltip>
            {form && shape && (
              <TextGeometryPopover
                shape={shape}
                geometryType={'Polygon'}
                form={form}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
